import { useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';

import { LogService } from '../services/log-service';

import { twelveHourUserSettingsNoTimeZoneUnpadded, twentyFourHourTimeUnpadded } from '../constants/timezone-format';
import Global from '../store/global-selectors';

const timezoneAcronymCode = 'ZZZZ'; // ex. CST, PST, CET, ACT...
const twelveHourPrefix = 'h';

export const useLuxonUserDateFormat = () => {
  const userDateFormat = useSelector(Global.selectUserUserDateFormat);
  return userDateFormat.replace(/D/g, 'd').replace(/Y/g, 'y');
};

const useTimeFormat = (includeTimezone) => {
  const userTimeFormat = useSelector(Global.selectUserUserTimeFormat);

  const is12Hour = userTimeFormat.startsWith(twelveHourPrefix);
  const baseFormat = is12Hour ? twelveHourUserSettingsNoTimeZoneUnpadded : twentyFourHourTimeUnpadded;
  return includeTimezone ? `${baseFormat} ${timezoneAcronymCode}` : baseFormat;
};

export const useSellerFormattedDate = (timestampWithOffset) => {
  const userDateFormat = useLuxonUserDateFormat();
  const sellerTimeZone = useSelector(Global.selectSellerTimeZoneIANA);

  if (!timestampWithOffset) {
    return;
  }
  return DateTime.fromISO(timestampWithOffset).setZone(sellerTimeZone).toFormat(userDateFormat);
};

export const useIsMidnightInSellerTimeZone = (timestampWithOffset) => {
  const sellerTimeZone = useSelector(Global.selectSellerTimeZoneIANA);

  if (!timestampWithOffset) {
    return false;
  }

  const time = DateTime.fromISO(timestampWithOffset).setZone(sellerTimeZone);

  // if time is midnight, don't show the time
  if (time.hour === 0 && time.minute === 0) {
    return true;
  }
  return false;
};

export const useLocalFormattedDate = (timestampWithOffset) => {
  const userDateFormat = useLuxonUserDateFormat();

  if (!timestampWithOffset) {
    return;
  }
  return DateTime.fromISO(timestampWithOffset).setZone('system').toFormat(userDateFormat);
};

export const useSellerFormattedTime = (timestampWithOffset, includeTimezone) => {
  const sellerTimeZone = useSelector(Global.selectSellerTimeZoneIANA);
  const timeFormat = useTimeFormat(includeTimezone);

  if (!timestampWithOffset) {
    return;
  }

  return DateTime.fromISO(timestampWithOffset).setZone(sellerTimeZone).toFormat(timeFormat);
};

export const useLocalFormattedTime = (timestampWithOffset, includeTimezone) => {
  const timeFormat = useTimeFormat(includeTimezone);

  if (!timestampWithOffset) {
    return;
  }

  return DateTime.fromISO(timestampWithOffset).setZone('system').toFormat(timeFormat);
};

export const useSellerFormattedDateTime = (timestampWithOffset, includeTimezone) => {
  const dateString = useSellerFormattedDate(timestampWithOffset);
  const timeString = useSellerFormattedTime(timestampWithOffset, includeTimezone);

  if (!timestampWithOffset) {
    return;
  }
  return `${dateString} ${timeString}`;
};

export const useLocalFormattedDateTime = (timestampWithOffset, includeTimezone) => {
  const dateString = useLocalFormattedDate(timestampWithOffset);
  const timeString = useLocalFormattedTime(timestampWithOffset, includeTimezone);

  if (!timestampWithOffset) {
    return;
  }
  return `${dateString} ${timeString}`;
};

/**
 * Hook for formatting UTC dates without timezone conversion.
 * Use this for dates where the date should display the same regardless of the user's timezone.
 * @param {string} isoDateString - The ISO date string to format in UTC
 * @returns {string} The formatted date string, or empty string if invalid or not provided
 */
export const useUtcFormattedDate = (isoDateString) => {
  const luxonDateFormat = useLuxonUserDateFormat();

  return useMemo(() => {
    try {
      if (!isoDateString) {
        return '';
      }

      const dateTime = DateTime.fromISO(isoDateString, { zone: 'utc' });

      if (!dateTime.isValid) {
        return '';
      }

      return dateTime.toFormat(luxonDateFormat);
    } catch (error) {
      LogService.logError(new Error('Error in useUtcFormattedDate', { cause: error }), { isoDateString });
      return '';
    }
  }, [isoDateString, luxonDateFormat]);
};

// ******** Legacy implementation below, prefer 'useLocalFormattedTime' or 'useSellerFormattedTime' ********

const OPTION_NUMERIC = 'numeric';
const OPTION_TWO_DIGIT = '2-digit';
const formatDateString = (userDateFormat, date, includeYear) => {
  const options = {
    year: includeYear ? OPTION_NUMERIC : undefined,
    month: OPTION_TWO_DIGIT,
    day: OPTION_TWO_DIGIT,
  };
  const includeYearAsTwoDigit = includeYear ? OPTION_TWO_DIGIT : undefined;
  switch (userDateFormat) {
    case 'DD/MM/YYYY':
      return new Intl.DateTimeFormat('en-GB', options).format(date);
    case 'DD/MM/YY':
      return new Intl.DateTimeFormat('en-GB', {
        ...options,
        year: includeYearAsTwoDigit,
      }).format(date);
    case 'MM/DD/YYYY':
      return new Intl.DateTimeFormat('en-US', options).format(date);
    case 'MM/DD/YY':
      return new Intl.DateTimeFormat('en-US', {
        ...options,
        year: includeYearAsTwoDigit,
      }).format(date);
    default:
      return new Intl.DateTimeFormat('en-GB', options).format(date);
  }
};
export const useUserDateFormat = () => {
  const format = useSelector(Global.selectUserUserDateFormat) || 'DD/MM/YYYY';
  const formatDate = useCallback(
    (date, includeYear = true) => {
      return formatDateString(format, new Date(date), includeYear).toString();
    },
    [format],
  );

  return useMemo(
    () => ({
      userDateFormat: format,
      formatUserDate: formatDate,
    }),
    [format, formatDate],
  );
};
